import { address as bitcoinAddress } from "bitcoinjs-lib";
import { Network } from "@/lib/networkConfig";

/**
 * Detects the Bitcoin network based on the address prefix.
 * @param address - The Bitcoin address to analyze.
 * @returns The detected network or 'unknown'.
 */
export function detectNetworkFromAddress(address: string): Network | "unknown" {
  // Define network parameters
  const networkParameters: Record<
    Network,
    { pubKeyHash: number; scriptHash: number; bech32: string }
  > = {
    mainnet: {
      pubKeyHash: 0x00, // Addresses start with '1'
      scriptHash: 0x05, // Addresses start with '3'
      bech32: "bc", // Bech32 addresses start with 'bc1'
    },
    signet: {
      pubKeyHash: 0x6f, // Testnet P2PKH addresses start with 'm' or 'n'
      scriptHash: 0xc4, // Testnet P2SH addresses start with '2'
      bech32: "tb", // Bech32 addresses start with 'tb1'
    },
    testnet3: {
      pubKeyHash: 0x6f,
      scriptHash: 0xc4,
      bech32: "tb",
    },
    testnet4: {
      pubKeyHash: 0x6f,
      scriptHash: 0xc4,
      bech32: "tb",
    },
    mutinynet: {
      pubKeyHash: 0x6f, // Adjust based on your network's specifications
      scriptHash: 0xc4, // Adjust based on your network's specifications
      bech32: "tb", // Custom Bech32 prefix for Mutinynet (e.g., 'mtb1...')
    },
    regtest: {
      pubKeyHash: 0x6f,
      scriptHash: 0xc4,
      bech32: "bcrt",
    },
  };

  for (const [network, params] of Object.entries(networkParameters)) {
    try {
      // Attempt to parse Base58Check addresses (P2PKH, P2SH)
      const decoded = bitcoinAddress.fromBase58Check(address);
      const version = decoded.version;

      if (version === params.pubKeyHash || version === params.scriptHash) {
        return network as Network;
      }
    } catch (e) {
      // Not a Base58Check address, try Bech32
      try {
        const decoded = bitcoinAddress.fromBech32(address);
        const prefix = decoded.prefix;

        if (prefix === params.bech32) {
          return network as Network;
        }
      } catch (e) {
        // Not a Bech32 address, continue to next network
        continue;
      }
    }
  }

  // If no matching network is found
  return "unknown";
}
